// Proactive Token Refresh Hook
// Refreshes the access token ~60s before it expires to prevent 401s

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/auth-store';
import type { ActivityTracker } from './useActivityTracker';
import { tryRefreshToken } from '../lib/trpc';

const REFRESH_BUFFER_MS = 60_000; // Refresh 60 seconds before expiry
const DEFAULT_IDLE_TIMEOUT_MINUTES = 480; // 8 hours default idle cutoff
export { tryRefreshToken } from '../lib/trpc';

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload)) as { exp?: number };
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to ms
  } catch {
    return null;
  }
}

export function useTokenRefresh(activityTracker: ActivityTracker): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { accessToken, expiresAt, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    // Determine expiry time: prefer stored expiresAt, fallback to JWT decode
    let expiryMs = expiresAt ? expiresAt * 1000 : null;
    if (!expiryMs) {
      expiryMs = decodeJwtExp(accessToken);
    }

    if (!expiryMs) {
      return; // Can't determine expiry, rely on reactive refresh
    }

    const now = Date.now();
    const refreshAt = expiryMs - REFRESH_BUFFER_MS;
    const delay = Math.max(refreshAt - now, 0);

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      // Check if user is idle before refreshing
      if (activityTracker.isIdle(DEFAULT_IDLE_TIMEOUT_MINUTES)) {
        // User has been idle too long; skip refresh.
        // Next user interaction will trigger a check.
        return;
      }

      await tryRefreshToken();
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [accessToken, expiresAt, isAuthenticated, activityTracker]);
}
